import Phaser from 'phaser';
import { HIGH_SCORE_KEY, HIGH_FLOOR_KEY, loadNumber } from '../storage';
import { tr } from '../i18n';
import { FocusNav } from '../ui/FocusNav';

export default class GameOverScene extends Phaser.Scene {
  private score = 0;
  private floor = 1;
  private nav!: FocusNav;
  private chosen = false;

  constructor() {
    super({ key: 'GameOverScene' });
  }

  init(data: { score?: number; floor?: number }) {
    this.score = data.score ?? 0;
    this.floor = data.floor ?? 1;
  }

  create() {
    const { width, height } = this.cameras.main;
    this.chosen = false;

    this.add.rectangle(0, 0, width, height, 0x000000, 0.7).setOrigin(0);

    this.add.text(width / 2, height / 2 - 120, tr('over.title'), {
      fontSize: '48px',
      fontFamily: 'monospace',
      color: '#e74c3c',
    }).setOrigin(0.5);

    this.add.text(width / 2, height / 2 - 54, tr('over.score', { score: this.score, floor: this.floor }), {
      fontSize: '26px',
      fontFamily: 'monospace',
      color: '#f1c40f',
    }).setOrigin(0.5);

    const highScore = loadNumber(HIGH_SCORE_KEY);
    const highFloor = loadNumber(HIGH_FLOOR_KEY);
    this.add.text(width / 2, height / 2 - 12, tr('over.best', { score: highScore, floor: highFloor }), {
      fontSize: '20px',
      fontFamily: 'monospace',
      color: '#2ecc71',
    }).setOrigin(0.5);

    // ── 两个选项：重新开始 / 返回主菜单 ─────────────────────
    const restartText = this.add.text(width / 2, height / 2 + 58, tr('over.restart'), {
      fontSize: '22px',
      fontFamily: 'monospace',
      color: '#ecf0f1',
      backgroundColor: '#1e2740',
      padding: { x: 24, y: 10 },
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    const menuText = this.add.text(width / 2, height / 2 + 118, tr('over.menu'), {
      fontSize: '22px',
      fontFamily: 'monospace',
      color: '#ecf0f1',
      backgroundColor: '#1e2740',
      padding: { x: 24, y: 10 },
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    const focus = (t: Phaser.GameObjects.Text) => t.setColor('#f1c40f');
    const blur = (t: Phaser.GameObjects.Text, fallback: string) => t.setColor(fallback);

    restartText.on('pointerover', () => focus(restartText));
    restartText.on('pointerout', () => blur(restartText, '#ecf0f1'));
    restartText.on('pointerdown', () => this.restart());
    menuText.on('pointerover', () => focus(menuText));
    menuText.on('pointerout', () => blur(menuText, '#ecf0f1'));
    menuText.on('pointerdown', () => this.backToMenu());

    // ── 键盘：↑↓ 选择，回车确认 ────────────────────────────
    this.nav = new FocusNav(this, 1, 5);
    this.nav.setItems([
      {
        row: 0, col: 0, target: restartText,
        onSelect: () => this.restart(),
        onFocus: () => focus(restartText), onBlur: () => blur(restartText, '#ecf0f1'),
      },
      {
        row: 1, col: 0, target: menuText,
        onSelect: () => this.backToMenu(),
        onFocus: () => focus(menuText), onBlur: () => blur(menuText, '#ecf0f1'),
      },
    ], 0);

    const kb = this.input.keyboard!;
    kb.on('keydown-UP', () => this.nav.move(0, -1));
    kb.on('keydown-DOWN', () => this.nav.move(0, 1));
    kb.on('keydown-LEFT', () => this.nav.move(-1, 0));
    kb.on('keydown-RIGHT', () => this.nav.move(1, 0));
    kb.on('keydown-ENTER', () => this.nav.select());
    kb.on('keydown-SPACE', () => this.restart());
    kb.on('keydown-ESC', () => this.backToMenu());
  }

  private restart() {
    if (this.chosen) return;
    this.chosen = true;
    this.scene.start('GameScene');
  }

  private backToMenu() {
    if (this.chosen) return;
    this.chosen = true;
    this.scene.start('MenuScene');
  }
}
